import { useCallback, useEffect, useState } from "react";
import {
  whatsNewApi,
  markDashboardSeen,
  WhatsNewItem,
  WhatsNewType,
} from "../services/whats-new.service";

// Map backend content types -> the dashboard card keys used in Dashboard.tsx,
// so callers can flag the matching cards / sidebar links.
const TYPE_TO_KEY: Record<WhatsNewType, string> = {
  release: "release",
  "product-sheet": "sheet",
  document: "document",
  "brand-guide": "brand",
  graphics: "graphics",
  webinar: "webinar",
  academy: "academy",
};

export interface UseWhatsNew {
  items: WhatsNewItem[];
  newCount: number;
  /** Set of content types (backend `type` values) with at least one new item. */
  newTypes: Set<WhatsNewType>;
  /** Same, but normalized to dashboard card keys (e.g. "product-sheet" -> "sheet"). */
  newKeys: Set<string>;
  loading: boolean;
  /** Mark the feed as seen (server + local) and clear the new badges locally. */
  markSeen: () => Promise<void>;
}

/**
 * Shared "What's New" feed. The backend resolves the "since" baseline from the
 * user's stored `lastDashboardSeenAt`, so we don't pass one — the count follows
 * the user across devices. Used by the dashboard, the sidebar bell, and the
 * per-section sidebar badges.
 */
export function useWhatsNew(limit = 30): UseWhatsNew {
  const [items, setItems] = useState<WhatsNewItem[]>([]);
  const [newCount, setNewCount] = useState(0);
  const [newTypes, setNewTypes] = useState<Set<WhatsNewType>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    whatsNewApi
      .getWhatsNew(undefined, limit)
      .then((res) => {
        if (!active) return;
        const data = res.data;
        setItems(data.items);
        setNewCount(data.newCount);
        setNewTypes(
          new Set(data.items.filter((i) => i.isNew).map((i) => i.type))
        );
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [limit]);

  const markSeen = useCallback(async () => {
    // Optimistically clear local badges; persist on the server.
    setNewCount(0);
    setNewTypes(new Set());
    markDashboardSeen(); // local fallback cache
    try {
      await whatsNewApi.markSeen();
    } catch {
      /* non-fatal: the server baseline just won't advance this time */
    }
  }, []);

  const newKeys = new Set(
    Array.from(newTypes).map((t) => TYPE_TO_KEY[t] ?? t)
  );

  return { items, newCount, newTypes, newKeys, loading, markSeen };
}
